import React, { ReactNode } from 'react';
import { Drawer, List, ListItem, ListItemText, Toolbar, CssBaseline, AppBar, Box, IconButton, Avatar, ListItemIcon, Collapse, Divider, Typography } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DashboardIcon from '@mui/icons-material/Dashboard';
import RequisitionIcon from '@mui/icons-material/Assignment';
import OrdersIcon from '@mui/icons-material/ShoppingCart';
import StakeholdersIcon from '@mui/icons-material/People';
import DevelopmentIcon from '@mui/icons-material/Build';
import UnitsIcon from '@mui/icons-material/Apartment';
import BomIcon from '@mui/icons-material/ListAlt';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ReportsIcon from '@mui/icons-material/BarChart';
import InventoryIcon from '@mui/icons-material/Inventory';
import ItemsIcon from '@mui/icons-material/Category';
import LocationIcon from '@mui/icons-material/Place';
import ItemLocationIcon from '@mui/icons-material/LocationCity';



import AllRequisitionsIcon from '@mui/icons-material/FormatListBulleted';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HistoryIcon from '@mui/icons-material/History';


import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';

import SwapHorizIcon from '@mui/icons-material/SwapHoriz';


import FolderIcon from '@mui/icons-material/Folder';
import SyncIcon from '@mui/icons-material/Sync';
import DescriptionIcon from '@mui/icons-material/Description';


import AddBoxIcon from '@mui/icons-material/AddBox';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SendIcon from '@mui/icons-material/AssignmentReturned';
import OutboxIcon from '@mui/icons-material/Outbox';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Link, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../../redux/hooks';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import config from "../../../config";

const drawerWidth = 240;

const Root = styled('div')({
  display: 'flex',
  height: '100vh',
});

const AppBarStyled = styled(AppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: 'transparent',
  boxShadow: 'none',
}));

const ToolbarStyled = styled(Toolbar)({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
});

const ToolbarRight = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const DrawerStyled = styled(Drawer)({
  width: drawerWidth,
  flexShrink: 0,
});

const DrawerPaper = styled('div')({
  width: drawerWidth,
  background: 'linear-gradient(180deg, #1C4697, #12315A)',
  height: '100%',
});

const DrawerContainer = styled('div')({
  height: 'calc(100% - 64px)',
  overflowY: 'auto',
  marginTop: '20px',
});

const CollapseContent = styled(Collapse)({
  width: '100%',
  '& .MuiCollapse-wrapperInner': {
    maxHeight: 'calc(100vh - 150px)',
    overflowY: 'auto',
  },
});

const Content = styled('main')(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
}));

const LinkStyled = styled(Link)({
  textDecoration: 'none',
  color: 'inherit',
});

const ActiveItemStyle = {
  backgroundColor: '#3A6EA5',
  fontWeight: 'bold',
  borderLeft: '5px solid #FFD700',
  paddingLeft: '20px',
};

interface ProcurementContainerProps {
  children: ReactNode;
}

type MenuEntry = {
  text: string;
  path: string;
  icon: React.ReactNode;
  disabled?: boolean;
};

const ProcurementContainer: React.FC<ProcurementContainerProps> = ({ children }) => {
  const user = useAppSelector((state) => state.auth.user);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [inventoryOpen, setInventoryOpen] = React.useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = React.useState(false);

  const [financeOpen, setFinanceOpen] = React.useState(false);
  const [bomOpen, setBomOpen] = React.useState(false);
  const [contractsOpen, setContractsOpen] = React.useState(false);
  const [reportsOpen, setReportsOpen] = React.useState(false);

  const bomItems = [
    { text: 'External Item Mapping', path: '/bom/external-item-mapping', icon: <SyncIcon /> }, // <-- Added
    { text: 'BOM Management', path: '/bom/management', icon: <DescriptionIcon /> },
  ];
  const contractItems = [
    { text: 'Contract List', path: '/procurement-contracts/management', icon: <DescriptionIcon /> },
  ];
  const reportItems = [
    { text: 'Cost To Completion', path: '/bom/reports/cost-to-completion', icon: <ReportsIcon /> },
    { text: 'BackOrderTracking', path: '/bom/reports/back-order-tracking', icon: <ReportsIcon /> },
    { text: 'Virtual Warehouse', path: '/procurement-contracts/virtual-warehouse', icon: <ReportsIcon /> },
  ];
  const menuItems: MenuEntry[] = [
    { text: 'Dashboard', path: '/procurement/', icon: <DashboardIcon /> },
    { text: 'Requisitions', path: '/procurement/requisitions', icon: <RequisitionIcon /> },
    { text: 'Purchase Orders', path: '/procurement/purchase-orders', icon: <OrdersIcon /> },
    { text: 'Stakeholders', path: '/procurement/stakeholders', icon: <StakeholdersIcon /> },
    { text: 'Developments', path: '/procurement/developments', icon: <DevelopmentIcon /> },
    { text: 'Units', path: '/procurement/units', icon: <UnitsIcon /> },
  ];

  const inventoryItems = [
    { text: 'Create Inventory', path: '/inventory/create-inventory', icon: <AddBoxIcon /> },
    { text: 'Item Categories', path: '/inventory/item-categories', icon: <FolderIcon /> },
    { text: 'Items', path: '/inventory/items', icon: <ItemsIcon /> },
    { text: 'Inventory Locations', path: '/inventory/inventory-locations', icon: <LocationIcon /> },
    { text: 'Inventory Movement', path: '/inventory/item-transfers', icon: <SwapHorizIcon /> },
    { text: 'Bin Locations', path: '/inventory/locations-stock', icon: <ItemLocationIcon /> },
    { text: 'Items Issued To Housing Units', path: '/inventory/items-issued', icon: <UnitsIcon /> },
    { text: 'Back Orders', path: '/inventory/back-orders', icon: <AssignmentLateIcon /> },
    { text: 'GRVs', path: '/inventory/grvs', icon: <SendIcon /> },
    { text: 'GIVs', path: '/inventory/givs', icon: <OutboxIcon /> },
  ];

  const financeItems = [
    { text: 'Payments Dashboard', path: '/finance/payment-requests-dashboard', icon: <ReportsIcon /> },
    { text: 'Pending Review', path: '/finance/pending-review', icon: <RequisitionIcon /> },
    { text: 'Payment Schedule', path: '/finance/payment-schedule', icon: <ScheduleIcon /> },
    { text: 'Prioritized Requests', path: '/finance/approved-requisitions', icon: <RequisitionIcon /> },
    { text: 'All Payment Requests', path: '/finance/payment-requisitions', icon: <AllRequisitionsIcon /> },
    { text: 'Payments History', path: '/finance/payments-history', icon: <HistoryIcon /> },

  ];

  const isActive = (path: string) =>
    location.pathname === path ||
    (location.pathname === '/' && path === '/procurement/procurement-process-dashboard');

  const handleInventoryClick = () => {
    setInventoryOpen(!inventoryOpen);
  };

  const handleFinanceClick = () => {
    setFinanceOpen(!financeOpen);
  };

  const handleBomClick = () => {
    setBomOpen(!bomOpen);
  };

  const handleContractsClick = () => {
    setContractsOpen(!contractsOpen);
  };

  const handleReportsClick = () => {
    setReportsOpen(!reportsOpen);
  };

  const handleDrawerToggle = () => {
    setMobileDrawerOpen((prev) => !prev);
  };

  const handleDrawerClose = () => {
    setMobileDrawerOpen(false);
  };

  const handleNavItemClick = () => {
    if (isMobile) {
      handleDrawerClose();
    }
  };

  React.useEffect(() => {
    const isInventoryPath = inventoryItems.some((item) => location.pathname.startsWith(item.path));
    if (isInventoryPath) {
      setInventoryOpen(true); // Keep the submenu open for inventory-related routes
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  React.useEffect(() => {
    const isFinancePath = financeItems.some((item) => location.pathname.startsWith(item.path)); // <-- Added
    if (isFinancePath) {
      setFinanceOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  
  React.useEffect(() => {
    const isBomPath = bomItems.some((item) => location.pathname.startsWith(item.path)); // <-- Added
    if (isBomPath) {
      setBomOpen(true);
    }
  }, [location.pathname]);

  React.useEffect(() => {
    const isContractsPath = contractItems.some((item) => location.pathname.startsWith(item.path));
    if (isContractsPath) {
      setContractsOpen(true);
    }
  }, [location.pathname]);

  React.useEffect(() => {
    const isReportsPath = reportItems.some((item) => location.pathname.startsWith(item.path));
    if (isReportsPath) {
      setReportsOpen(true);
    }
  }, [location.pathname]);

  React.useEffect(() => {
    if (isMobile) {
      handleDrawerClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isMobile]);

  const drawerContent = (
    <List>
      {menuItems.map((item) => (
        <ListItem
          key={item.text}
          component={item.disabled ? 'div' : LinkStyled}
          to={item.disabled ? undefined : item.path}
          onClick={handleNavItemClick}
          sx={{
            opacity: item.disabled ? 0.5 : 1,
            color: item.disabled ? 'grey.400' : 'white',
            pointerEvents: item.disabled ? 'none' : 'auto',
            padding: '15px',
            '&:hover': {
              backgroundColor: item.disabled ? 'inherit' : '#2E5E8C',
              transition: 'background-color 0.3s ease',
            },
            ...(isActive(item.path) && !item.disabled && ActiveItemStyle),
          }}
        >
          <ListItemIcon sx={{ color: item.disabled ? 'grey.400' : 'white' }}>
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography sx={{ color: item.disabled ? 'grey.400' : 'white' }}>
                {item.text}
              </Typography>
            }
          />
        </ListItem>
      ))}

      {/* 👇 Bill of Materials submenu */}
      <ListItem onClick={handleBomClick}>
        <ListItemIcon sx={{ color: 'white' }}>
          <BomIcon />
        </ListItemIcon>
        <ListItemText primary={<Typography sx={{ color: 'white' }}>Bill of Materials</Typography>} />
        {bomOpen ? <ExpandLessIcon sx={{ color: 'white' }} /> : <ExpandMoreIcon sx={{ color: 'white' }} />}
      </ListItem>
      <CollapseContent in={bomOpen} timeout="auto" unmountOnExit>
        <Divider sx={{ backgroundColor: '#3A6EA5' }} />
        <List component="div" disablePadding>
          {bomItems.map((item) => (
            <ListItem
              component={LinkStyled}
              to={item.path}
              onClick={handleNavItemClick}
              key={item.text}
              sx={{
                pl: 6,
                '&:hover': {
                  backgroundColor: '#2E5E8C',
                  transition: 'background-color 0.3s ease',
                },
                ...(isActive(item.path) && ActiveItemStyle),
              }}
            >
              <ListItemIcon sx={{ color: 'white' }}>{item.icon}</ListItemIcon>
              <ListItemText primary={<Typography sx={{ color: 'white' }}>{item.text}</Typography>} />
            </ListItem>
          ))}
        </List>
        <Divider sx={{ backgroundColor: '#3A6EA5' }} />
      </CollapseContent>
      {/* End Bill of Materials submenu */}

      {/* 👇 Procurement Contracts submenu */}
      <ListItem onClick={handleContractsClick}>
        <ListItemIcon sx={{ color: 'white' }}>
          <HandshakeIcon />
        </ListItemIcon>
        <ListItemText primary={<Typography sx={{ color: 'white' }}>Procurement Contracts</Typography>} />
        {contractsOpen ? <ExpandLessIcon sx={{ color: 'white' }} /> : <ExpandMoreIcon sx={{ color: 'white' }} />}
      </ListItem>
      <CollapseContent in={contractsOpen} timeout="auto" unmountOnExit>
        <Divider sx={{ backgroundColor: '#3A6EA5' }} />
        <List component="div" disablePadding>
          {contractItems.map((item) => (
            <ListItem
              component={LinkStyled}
              to={item.path}
              onClick={handleNavItemClick}
              key={item.text}
              sx={{
                pl: 6,
                '&:hover': {
                  backgroundColor: '#2E5E8C',
                  transition: 'background-color 0.3s ease',
                },
                ...(isActive(item.path) && ActiveItemStyle),
              }}
            >
              <ListItemIcon sx={{ color: 'white' }}>{item.icon}</ListItemIcon>
              <ListItemText primary={<Typography sx={{ color: 'white' }}>{item.text}</Typography>} />
            </ListItem>
          ))}
        </List>
        <Divider sx={{ backgroundColor: '#3A6EA5' }} />
      </CollapseContent>
      {/* End Procurement Contracts submenu */}

      <ListItem onClick={handleReportsClick}>
        <ListItemIcon sx={{ color: 'white' }}>
          <ReportsIcon />
        </ListItemIcon>
        <ListItemText primary={<Typography sx={{ color: 'white' }}>Reports</Typography>} />
        {reportsOpen ? <ExpandLessIcon sx={{ color: 'white' }} /> : <ExpandMoreIcon sx={{ color: 'white' }} />}
      </ListItem>
      <CollapseContent in={reportsOpen} timeout="auto" unmountOnExit>
        <Divider sx={{ backgroundColor: '#3A6EA5' }} />
        <List component="div" disablePadding>
          {reportItems.map((item) => (
            <ListItem
              component={LinkStyled}
              to={item.path}
              onClick={handleNavItemClick}
              key={item.text}
              sx={{
                pl: 6,
                '&:hover': {
                  backgroundColor: '#2E5E8C',
                  transition: 'background-color 0.3s ease',
                },
                ...(isActive(item.path) && ActiveItemStyle),
              }}
            >
              <ListItemIcon sx={{ color: 'white' }}>{item.icon}</ListItemIcon>
              <ListItemText primary={<Typography sx={{ color: 'white' }}>{item.text}</Typography>} />
            </ListItem>
          ))}
        </List>
        <Divider sx={{ backgroundColor: '#3A6EA5' }} />
      </CollapseContent>

      <ListItem onClick={handleInventoryClick}>
        <ListItemIcon sx={{ color: 'white' }}>
          <InventoryIcon />
        </ListItemIcon>
        <ListItemText primary={<Typography sx={{ color: 'white' }}>Inventory</Typography>} />
        {inventoryOpen ? <ExpandLessIcon sx={{ color: 'white' }} /> : <ExpandMoreIcon sx={{ color: 'white' }} />}
      </ListItem>
      <CollapseContent in={inventoryOpen} timeout="auto" unmountOnExit>
        <Divider sx={{ backgroundColor: '#3A6EA5' }} />
        <List component="div" disablePadding>
          {inventoryItems.map((item) => (
            <ListItem
              component={LinkStyled}
              to={item.path}
              onClick={handleNavItemClick}
              key={item.text}
              sx={{
                pl: 6,
                '&:hover': {
                  backgroundColor: '#2E5E8C',
                  transition: 'background-color 0.3s ease',
                },
                ...(isActive(item.path) && ActiveItemStyle),
              }}
            >
              <ListItemIcon sx={{ color: 'white' }}>{item.icon}</ListItemIcon>
              <ListItemText primary={<Typography sx={{ color: 'white' }}>{item.text}</Typography>} />
            </ListItem>
          ))}
        </List>
        <Divider sx={{ backgroundColor: '#3A6EA5' }} />
      </CollapseContent>

      {/*  Finance submenu */}
      <ListItem onClick={handleFinanceClick}>
        <ListItemIcon sx={{ color: 'white' }}>
          <ReceiptIcon />
        </ListItemIcon>
        <ListItemText primary={<Typography sx={{ color: 'white' }}>Finance</Typography>} />
        {financeOpen ? <ExpandLessIcon sx={{ color: 'white' }} /> : <ExpandMoreIcon sx={{ color: 'white' }} />}
      </ListItem>
      <CollapseContent in={financeOpen} timeout="auto" unmountOnExit>
        <Divider sx={{ backgroundColor: '#3A6EA5' }} />
        <List component="div" disablePadding>
          {financeItems.map((item) => (
            <ListItem
              component={LinkStyled}
              to={item.path}
              onClick={handleNavItemClick}
              key={item.text}
              sx={{
                pl: 6,
                '&:hover': {
                  backgroundColor: '#2E5E8C',
                  transition: 'background-color 0.3s ease',
                },
                ...(isActive(item.path) && ActiveItemStyle),
              }}
            >
              <ListItemIcon sx={{ color: 'white' }}>{item.icon}</ListItemIcon>
              <ListItemText primary={<Typography sx={{ color: 'white' }}>{item.text}</Typography>} />
            </ListItem>
          ))}
        </List>
        <Divider sx={{ backgroundColor: '#3A6EA5' }} />
      </CollapseContent>
      {/* End Finance submenu */}
    </List>
  );

  return (
    <Root>
      <CssBaseline />
      <AppBarStyled position="fixed">
        <ToolbarStyled>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isMobile && (
              <IconButton color="primary" onClick={handleDrawerToggle} aria-label="open navigation menu">
                <MenuIcon sx={{ color: '#1C4697' }} />
              </IconButton>
            )}
            <img
              alt="WestProp Logo"
              src={config.whiteLogo}
              style={{ marginRight: 20, width: isMobile ? 120 : 150 }}
            />
          </Box>
          <ToolbarRight>
            <IconButton color="inherit">
              <NotificationsIcon sx={{ color: '#1C4697' }} />
            </IconButton>
            <IconButton sx={{ p: 0 }}>
              <Avatar>{user?.name?.slice(0, 1) ?? ''}</Avatar>
            </IconButton>
          </ToolbarRight>
        </ToolbarStyled>
      </AppBarStyled>
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileDrawerOpen}
          onClose={handleDrawerClose}
          ModalProps={{ keepMounted: true }}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 2,
            '& .MuiDrawer-paper': {
              zIndex: (theme) => theme.zIndex.drawer + 2,
              width: drawerWidth,
              background: 'linear-gradient(180deg, #1C4697, #12315A)',
            },
          }}
        >
          <Toolbar sx={{ justifyContent: 'flex-end' }}>
            <IconButton onClick={handleDrawerClose} aria-label="close navigation menu" sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
          <DrawerContainer>{drawerContent}</DrawerContainer>
        </Drawer>
      ) : (
        <DrawerStyled variant="permanent">
          <DrawerPaper>
            <Toolbar />
            <DrawerContainer>{drawerContent}</DrawerContainer>
          </DrawerPaper>
        </DrawerStyled>
      )}
      <Content>
        <Toolbar />
        <Box mx={'20px'}>{children}</Box>
      </Content>
    </Root>
  );
};

export default ProcurementContainer;
